import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import { ShowSnackbar } from './../../shared/shared.actions'
import {
  CreateDataCycleFailure,
  CreateDataCycleRequest,
  CreateDataCycleSuccess,
  DeleteDataCycleFailure,
  DeleteDataCycleRequest,
  DeleteDataCycleSuccess,
  FetchBatchTaskStatusFailure,
  FetchBatchTaskStatusRequest,
  FetchBatchTaskStatusSuccess,
  FetchDataCycleFailure,
  FetchDataCycleRequest,
  FetchDataCycleStatsFailure,
  FetchDataCycleStatsRequest,
  FetchDataCycleStatsSuccess,
  FetchDataCycleSuccess,
  FetchDataCyclesFailure,
  FetchDataCyclesRequest,
  FetchDataCyclesSuccess,
  FetchInitSwitchStatsFailure,
  FetchInitSwitchStatsRequest,
  FetchInitSwitchStatsSuccess,
  StopDataCycleSwitchingFailure,
  StopDataCycleSwitchingRequest,
  StopDataCycleSwitchingSuccess,
  SwitchDataCycleFailure,
  SwitchDataCycleRequest,
  SwitchDataCycleSuccess,
  UpdateDataCycleFailure,
  UpdateDataCycleRequest,
  UpdateDataCycleSuccess,
} from './data-cycles.actions'
import { DataCyclesService } from './data-cycles.service'
import * as DataCyclesTypes from './data-cycles.types'

@Injectable()
export class DataCyclesEffects {
  constructor(
    private actions: Actions,
    private dataCyclesService: DataCyclesService,
    private router: Router,
  ) {}

  FetchDataCycles$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.FETCH_DATA_CYCLES_REQUEST),
      switchMap((action: FetchDataCyclesRequest) => {
        return this.dataCyclesService.fetchDataCycles(action.payload).pipe(
          map((result) => {
            return new FetchDataCyclesSuccess(
              result.data.results,
              result.data.totalCount,
              result.data.filteredCount,
            )
          }),
          catchError((err, caught) => {
            return of(new FetchDataCyclesFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchDataCycle$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.FETCH_DATA_CYCLE_REQUEST),
      switchMap((action: FetchDataCycleRequest) => {
        return this.dataCyclesService.fetchDataCycle(action.id).pipe(
          map((result) => {
            return new FetchDataCycleSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchDataCycleFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DeleteDataCycle$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.DELETE_DATA_CYCLE_REQUEST),
      switchMap((action: DeleteDataCycleRequest) => {
        return this.dataCyclesService.deleteDataCycle(action.id).pipe(
          switchMap((result) => {
            return [
              new DeleteDataCycleSuccess(),
              new ShowSnackbar({
                type: 'success',
                message: 'Data cycle deleted successfully.',
              }),
            ]
          }),
          catchError((err, caught) => {
            return of(new DeleteDataCycleFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  CreateDataCycle$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.CREATE_DATA_CYCLE_REQUEST),
      switchMap((action: CreateDataCycleRequest) => {
        return this.dataCyclesService.createDataCycle(action.payload).pipe(
          switchMap((result) => {
            this.router.navigate(['/admin/data-cycles'])
            return [new CreateDataCycleSuccess()]
          }),
          catchError((err, caught) => {
            return of(new CreateDataCycleFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  UpdateDataCycle$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.UPDATE_DATA_CYCLE_REQUEST),
      switchMap((action: UpdateDataCycleRequest) => {
        return this.dataCyclesService
          .updateDataCycle(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              this.router.navigate(['/admin/data-cycles'])
              return [
                new FetchDataCycleRequest(action.id),
                new UpdateDataCycleSuccess(),
              ]
            }),
            catchError((err, caught) => {
              return of(new UpdateDataCycleFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  FetchBatchTaskStatus$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.FETCH_BATCH_TASK_STATUS_REQUEST),
      switchMap((action: FetchBatchTaskStatusRequest) => {
        return this.dataCyclesService.fetchBatchTaskStatus().pipe(
          map((result) => {
            return new FetchBatchTaskStatusSuccess(result)
          }),
          catchError((err, caught) => {
            return of(new FetchBatchTaskStatusFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  SwitchDataCycle$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.SWITCH_DATA_CYCLE_REQUEST),
      switchMap((action: SwitchDataCycleRequest) => {
        return this.dataCyclesService.switchDataCycle(action.id).pipe(
          switchMap((result) => {
            return [
              new SwitchDataCycleSuccess(result.data),
              new FetchBatchTaskStatusRequest(),
            ]
          }),
          catchError((err, caught) => {
            return of(new SwitchDataCycleFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  StopDataCycleSwitching$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.STOP_DATA_CYCLE_SWITCHING_REQUEST),
      switchMap((action: StopDataCycleSwitchingRequest) => {
        return this.dataCyclesService.stopDataCycleSwitching().pipe(
          switchMap((result) => {
            return [
              new StopDataCycleSwitchingSuccess(result.data),
              new FetchBatchTaskStatusRequest(),
            ]
          }),
          catchError((err, caught) => {
            return of(new StopDataCycleSwitchingFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchDataCycleStats$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.FETCH_DATA_CYCLE_STATS_REQUEST),
      switchMap((action: FetchDataCycleStatsRequest) => {
        return this.dataCyclesService.fetchDataCycleStats().pipe(
          map((result) => {
            return new FetchDataCycleStatsSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchDataCycleStatsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchInitSwitchStats$ = createEffect(() =>
    this.actions.pipe(
      ofType(DataCyclesTypes.FETCH_INIT_SWITCH_STATS_REQUEST),
      switchMap((action: FetchInitSwitchStatsRequest) => {
        return this.dataCyclesService.fetchInitSwitchStats().pipe(
          map((result) => {
            return new FetchInitSwitchStatsSuccess(result)
          }),
          catchError((err, caught) => {
            return of(new FetchInitSwitchStatsFailure({ error: err }))
          }),
        )
      }),
    ),
  )
}
